/**
 * "The page is going away" as a first-class fact for the API client.
 *
 * A fetch that the browser cancelled because WE navigated is not a failure, but
 * it rejects with exactly the same TypeError a dead network does, so client.ts
 * reported it as one.
 *
 * Measured on production 2026-08-20: user-2f73bc@example.com finished
 * onboarding at 16:22:35.149. The dashboard rendered at 16:22:35.596, fired its
 * four mount queries (/dashboard/summary, /projects, /notifications,
 * /whats-new/current), and all four rejected inside a 6ms window at
 * 16:22:35.661-.667 — 65ms after mount, far too fast and far too uniform to be
 * a real network failure. 485ms later she was on api.overlayqa.com, because
 * onboardingFinish.ts had called window.location.assign() cross-origin to send
 * her to her first team review.
 *
 * Nothing was broken and she saw nothing: the navigation was the whole point of
 * the feature. The cost landed on us — four phantom `api_network_failure`
 * events, two of which survived `retry: 1` to reach main.tsx's QueryCache
 * handler and became `$exception`s. That is the same fake-crash class PRs
 * #66/#67/#68 were opened to clean out of Error Tracking.
 *
 * WHY `pagehide` AND NOT THE OBVIOUS ALTERNATIVES:
 *
 *  - `beforeunload` fires EARLIER, which is tempting, but it can be cancelled
 *    (a confirm dialog, or a handler that returns a string). A page that stays
 *    would be stranded with the flag latched true and would silently stop
 *    reporting real failures for the rest of its life. That is a worse bug than
 *    the one being fixed, so beforeunload is deliberately not wired.
 *  - `visibilitychange` fires on a plain tab switch. A backgrounded tab is
 *    still alive and its failures are still real; muting them would hide an
 *    outage. Also deliberately not wired.
 *  - `pageshow` clears the flag, so a page restored from bfcache is not left
 *    permanently mute. This is why the flag is not write-once.
 *
 * Pure module: no import.meta.env, no logger, no posthog, so it loads under
 * jest. client.ts cannot (module-scope import.meta.env), which is why the
 * wiring is pinned by a source-level guard in
 * __tests__/client.pageUnloading.test.ts. Same pattern as networkError.ts /
 * networkFailure.ts / requestTimeout.ts.
 */

/**
 * Thrown instead of a NetworkError when a request is cancelled by our own
 * navigation.
 *
 * The type is what keeps the fake crash out: main.tsx raises `$exception` only
 * for `isNetworkError(err)` (see networkFailure.ts), and this is not a
 * NetworkError, so a cancelled-by-navigation rejection can never mint one.
 * Nobody reads the message — by definition the page rendering it is gone.
 */
export class PageUnloadingError extends Error {
  constructor() {
    super('Request cancelled because the page navigated away.');
    this.name = 'PageUnloadingError';
  }
}

let unloading = false;
let exitCount = 0;
let installed = false;

/** True once the page has begun navigating away or being discarded. */
export function isPageUnloading(): boolean {
  return unloading;
}

/** Distinguishes an old deferred read from a new read after cache restoration. */
export function getPageExitCount(): number {
  return exitCount;
}

/**
 * Wire the flag to the page lifecycle. Idempotent, and a no-op without a
 * window so a non-DOM context (SSR, a node script importing the client) cannot
 * break on import.
 */
export function installPageUnloadListeners(target: Window | undefined = typeof window !== 'undefined' ? window : undefined): void {
  if (!target || installed) return;
  installed = true;
  target.addEventListener('pagehide', () => {
    unloading = true;
    exitCount++;
  });
  target.addEventListener('pageshow', () => {
    unloading = false;
  });
}

/** Test-only. Resets both the flag and the install latch. */
export function __resetPageUnloadingForTest(): void {
  unloading = false;
  exitCount = 0;
  installed = false;
}
